import React, { useMemo, useState } from 'react';
import { Card } from 'primereact/card';
import { Panel } from 'primereact/panel';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Message } from 'primereact/message';
import { TabView, TabPanel } from 'primereact/tabview';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Chart } from 'primereact/chart';
import { NoRecordsCard } from './_SmartFieldRenderer';

// Indian-standard money formatting (mandatory — ui-ux skill).
const formatINR = (amount) => {
    const n = parseFloat(amount);
    if (!Number.isFinite(n)) return '—';
    return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

const formatNum = (n) => {
    const v = parseFloat(n);
    return Number.isFinite(v) ? v.toLocaleString('en-IN') : '—';
};

// Compact form for chart axes / tiles: 1.2Cr, 45.6L, 78.9K
const formatCompact = (amount) => {
    const n = parseFloat(amount);
    if (!Number.isFinite(n)) return '—';
    const abs = Math.abs(n);
    if (abs >= 1e7) return `₹${(n / 1e7).toFixed(2)}Cr`;
    if (abs >= 1e5) return `₹${(n / 1e5).toFixed(2)}L`;
    if (abs >= 1e3) return `₹${(n / 1e3).toFixed(1)}K`;
    return formatINR(n);
};

// GLIB section keys, verified against a real 12-statement report.
const K = {
    scorecard: 'Summary - Scorecard',
    obligation: 'Summary - Fixed Income / Obligation',
    authenticity: 'Summary - Document Authenticity Check',
    fraudChecks: 'Fraud Check Triggers - Fraud Checks',
    fraudDetails: 'Fraud Check Triggers - Fraud Check Details',
    emi: 'Emi Details',
    eod: 'EOD',
    txns: 'All Transactions',
    returns: 'Return Transactions',
    recurringDr: 'Recurring Debits',
    recurringCr: 'Recurring Credits',
    scoring: 'Scoring Details',
    topCredit: 'Top Transactions - Top 10 Credit Transaction',
    topDebit: 'Top Transactions - Top 10 Debit Transaction',
    mwdBalance: 'MWD - Balance Details',
    mwdInflow: 'MWD - Inflow Details',
    mwdOutflow: 'MWD - Outflow Details',
    mwdBounce: 'MWD - Bounce Details',
};

const StatTile = ({ icon, label, value, sub, severity }) => (
    <div className="col-12 md:col-6 lg:col-3">
        <div className="card mb-0 h-full">
            <div className="flex justify-content-between align-items-start">
                <div className="flex flex-column gap-2 min-w-0">
                    <span className="text-600 text-sm font-medium">{label}</span>
                    <span className={`text-xl font-semibold ${severity === 'danger' ? 'text-red-500' : severity === 'success' ? 'text-green-600' : 'text-900'}`}>
                        {value}
                    </span>
                </div>
                <div className="flex align-items-center justify-content-center border-round bg-primary-50 p-2 flex-shrink-0">
                    <i className={`pi ${icon} text-xl text-primary`} aria-hidden="true" />
                </div>
            </div>
            {sub && <span className="text-500 text-xs block mt-2">{sub}</span>}
        </div>
    </div>
);

// Label / value row used inside Panels.
const InfoItem = ({ label, value, money }) => (
    <div className="col-12 md:col-6 lg:col-3">
        <div className="surface-100 border-round p-3 h-full">
            <div className="text-600 text-xs font-medium mb-1">{label}</div>
            <div className="text-900 text-base font-semibold">
                {money ? formatINR(value) : (value ?? '—')}
            </div>
        </div>
    </div>
);

const chartOptions = (compact = true) => ({
    maintainAspectRatio: false,
    plugins: {
        legend: { labels: { color: 'var(--text-color-secondary)', usePointStyle: true, boxWidth: 8 } },
        tooltip: {
            callbacks: {
                label: (ctx) => `${ctx.dataset.label || ''}: ${formatINR(ctx.parsed.y ?? ctx.parsed)}`,
            },
        },
    },
    scales: {
        x: { ticks: { color: 'var(--text-color-secondary)', maxRotation: 45, minRotation: 0 }, grid: { color: 'var(--surface-border)' } },
        y: {
            ticks: {
                color: 'var(--text-color-secondary)',
                callback: (v) => (compact ? formatCompact(v) : v),
            },
            grid: { color: 'var(--surface-border)' },
        },
    },
});

// Generic table for any GLIB list-of-objects section. Columns come from the
// first row so a new vendor field shows up instead of being silently dropped.
const AutoTable = ({ rows, moneyKeys = [], frozenKey, emptyText = 'No records', note }) => {
    if (!Array.isArray(rows) || rows.length === 0) {
        return (
            <div className="flex flex-column align-items-center py-4">
                <i className="pi pi-inbox pi-2x text-400 mb-2" aria-hidden="true" />
                <span className="text-600 text-sm">{emptyText}</span>
            </div>
        );
    }

    const cols = Object.keys(rows[0]);
    const money = new Set(moneyKeys);

    const bodyFor = (key) => (row) => {
        const v = row[key];
        if (v === null || v === undefined || v === '') return <span className="text-500">—</span>;
        if (money.has(key)) return <span className="font-medium">{formatINR(v)}</span>;
        if (key === 'Credit/Debit') {
            return <Tag severity={String(v).toLowerCase() === 'credit' ? 'success' : 'danger'} value={String(v)} className="text-xs" />;
        }
        return <span className="text-900">{String(v)}</span>;
    };

    return (
        <>
            {note && <p className="text-500 text-xs mt-0 mb-2">{note}</p>}
            <DataTable
                value={rows}
                paginator
                rows={10}
                rowsPerPageOptions={[10, 25, 50]}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
                currentPageReportTemplate="Showing {first}-{last} of {totalRecords}"
                paginatorClassName="text-sm"
                pageLinkSize={3}
                size="small"
                stripedRows
                showGridlines
                scrollable
                scrollHeight="flex"
                responsiveLayout="scroll"
                className="p-datatable-gridlines"
            >
                {cols.map((c, i) => (
                    <Column
                        key={c}
                        field={c}
                        header={c}
                        body={bodyFor(c)}
                        sortable
                        frozen={frozenKey ? c === frozenKey : i === 0}
                        alignFrozen="left"
                        style={{ minWidth: c === 'Description' ? '260px' : '130px' }}
                    />
                ))}
            </DataTable>
        </>
    );
};

/**
 * GLIB (Autosift) Bank Statement Analysis — output component.
 *
 * Renders the stored analysis as a visual dashboard, following the same shape as
 * BankStatementOutputComponent (BoostMoney BSA): Panel sections, summary tiles,
 * charts and month-wise tables.
 *
 * Deliberately NO raw-JSON download — the user reads the analysis in the UI. The
 * Excel workbook stays available because it is the artefact credit teams file and
 * share; the JSON is an implementation detail.
 */
const GlibBankStatementOutputComponent = ({
    reportData,
    hideCredits,
    isSharedView,
}) => {
    const shouldHideCredits = hideCredits || isSharedView;
    const [downloading, setDownloading] = useState(false);
    // Detail is collapsed by default: the tiles answer "did it work / what are
    // the headline numbers", and the full breakdown is opt-in so the page does
    // not open as a wall of tables.
    const [expanded, setExpanded] = useState(false);

    const json = useMemo(() => {
        const rj = reportData?.report_json;
        if (!rj) return {};
        if (typeof rj !== 'string') return rj;
        try { return JSON.parse(rj); } catch { return {}; }
    }, [reportData]);

    const meta = useMemo(() => {
        if (!reportData) return null;
        return {
            isComplete: reportData.is_complete ?? (json.stage === 'completed'),
            isPartial: reportData.is_partial ?? json.is_partial ?? false,
            analysed: reportData.statements_analysed ?? json.statements_analysed ?? null,
            failed: reportData.statements_failed ?? json.statements_failed ?? 0,
            vendorStatus: reportData.vendor_status ?? json.latest_work_order?.status ?? null,
            workOrderId: reportData.work_order_id ?? json.work_order_id ?? null,
            reportId: reportData.report_id ?? reportData.id ?? null,
            excelUrl: (reportData.download_urls ?? json.download_urls ?? {})?.excel ?? null,
            remaining: reportData.remaining_credits ?? undefined,
        };
    }, [reportData, json]);

    const analysis = useMemo(() => {
        if (!reportData) return null;
        if (reportData[K.txns] || reportData[K.scorecard]) return reportData;   // raw vendor json
        if (json.analysis) return json.analysis;                                 // stored (normal)
        if (json[K.txns] || json[K.scorecard]) return json;
        return null;
    }, [reportData, json]);

    // ---- derived values (all guarded; a missing section must not crash) ----
    // Memoised together: these feed the chart useMemos below, so deriving them
    // inline would hand those a fresh array reference on every render and defeat
    // the memoisation entirely (see ui-ux P14).
    const s = useMemo(() => {
        const list = (key) => (Array.isArray(analysis?.[key]) ? analysis[key] : []);
        return {
            obligation: (analysis?.[K.obligation] && typeof analysis[K.obligation] === 'object') ? analysis[K.obligation] : {},
            mwdBalance: list(K.mwdBalance),
            mwdInflow: list(K.mwdInflow),
            mwdOutflow: list(K.mwdOutflow),
            mwdBounce: list(K.mwdBounce),
            eod: (analysis?.[K.eod] && typeof analysis[K.eod] === 'object') ? analysis[K.eod] : {},
            truncated: analysis?._truncated || {},
        };
    }, [analysis]);

    const { obligation, mwdBalance, mwdInflow, mwdOutflow, mwdBounce, eod, truncated } = s;

    const months = useMemo(
        () => mwdBalance.map((r) => r.Month).filter(Boolean),
        [mwdBalance],
    );

    const totals = useMemo(() => {
        const credit = mwdInflow.reduce((a, r) => a + (parseFloat(r['Total Credit']) || 0), 0);
        const debit = mwdOutflow.reduce((a, r) => a + (parseFloat(r['Total Debits']) || 0), 0);
        const bounces = mwdBounce.reduce(
            (a, r) => a + (parseInt(r['Total Inward Cheque Bounce'], 10) || 0) + (parseInt(r['Total Outward Cheque Bounce'], 10) || 0),
            0,
        );
        const closing = mwdBalance.length ? mwdBalance[mwdBalance.length - 1]['Monthly Closing Balance'] : null;
        const opening = mwdBalance.length ? mwdBalance[0]['Monthly Opening Balance'] : null;
        return { credit, debit, bounces, opening, closing, net: credit - debit };
    }, [mwdInflow, mwdOutflow, mwdBounce, mwdBalance]);

    // Balance trend (monthly avg + closing)
    const balanceChart = useMemo(() => ({
        labels: months,
        datasets: [
            {
                label: 'Average Balance',
                data: mwdBalance.map((r) => parseFloat(r['Average Bank Balance']) || 0),
                borderColor: 'var(--primary-color)',
                backgroundColor: 'transparent',
                tension: 0.35,
                fill: false,
            },
            {
                label: 'Closing Balance',
                data: mwdBalance.map((r) => parseFloat(r['Monthly Closing Balance']) || 0),
                borderColor: 'var(--green-500)',
                backgroundColor: 'transparent',
                borderDash: [5, 4],
                tension: 0.35,
                fill: false,
            },
        ],
    }), [months, mwdBalance]);

    // Inflow vs outflow per month
    const flowChart = useMemo(() => ({
        labels: months,
        datasets: [
            {
                label: 'Inflow',
                data: mwdInflow.map((r) => parseFloat(r['Total Credit']) || 0),
                backgroundColor: 'var(--green-400)',
            },
            {
                label: 'Outflow',
                data: mwdOutflow.map((r) => parseFloat(r['Total Debits']) || 0),
                backgroundColor: 'var(--red-400)',
            },
        ],
    }), [months, mwdInflow, mwdOutflow]);

    // Daily EOD balance — thin the series so a 372-point line stays readable.
    const eodChart = useMemo(() => {
        const keys = Object.keys(eod);
        if (keys.length === 0) return null;
        const step = Math.max(1, Math.ceil(keys.length / 120));
        const picked = keys.filter((_, i) => i % step === 0);
        return {
            labels: picked,
            datasets: [{
                label: 'End-of-day Balance',
                data: picked.map((k) => parseFloat(eod[k]) || 0),
                borderColor: 'var(--primary-color)',
                backgroundColor: 'transparent',
                borderWidth: 1.5,
                pointRadius: 0,
                tension: 0.25,
                fill: false,
            }],
        };
    }, [eod]);

    if (!reportData) return <NoRecordsCard subtitle="No bank statement analysis available" />;

    const downloadExcel = () => {
        if (!meta?.excelUrl) return;
        setDownloading(true);
        window.open(meta.excelUrl, '_blank', 'noopener,noreferrer');
        setTimeout(() => setDownloading(false), 800);
    };

    return (
        <>
            {meta?.isPartial && (
                <Message
                    severity="warn"
                    className="w-full mb-3"
                    content={
                        <div className="text-sm">
                            <div className="font-medium mb-1">Partial analysis</div>
                            <div className="text-700">
                                Only {meta.analysed} statement(s) were analysed
                                {meta.failed ? ` — ${meta.failed} failed to upload` : ''}.
                                Treat these figures as covering an incomplete period.
                            </div>
                        </div>
                    }
                />
            )}

            {/* ---------------------------------------------------- header */}
            <Card className="mb-3">
                <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-2 mb-3">
                    <h2 className="text-lg font-semibold text-900 m-0">
                        <i className="pi pi-chart-line mr-2 text-primary" aria-hidden="true" />
                        Bank Statement Analysis
                    </h2>
                    <div className="flex flex-wrap align-items-center gap-2">
                        {meta?.vendorStatus && (
                            <Tag
                                severity={meta.isComplete ? 'success' : 'info'}
                                icon={meta.isComplete ? 'pi pi-check-circle' : 'pi pi-clock'}
                                value={meta.isComplete ? 'Completed' : String(meta.vendorStatus)}
                                className="text-sm"
                            />
                        )}
                        {!shouldHideCredits && meta?.remaining !== undefined && meta.remaining !== null && (
                            <Tag severity="success" icon="pi pi-wallet" value={`Remaining: ${meta.remaining}`} className="text-sm" />
                        )}
                    </div>
                </div>

                <div className="grid">
                    <StatTile
                        icon="pi-file-pdf"
                        label="Statements Analysed"
                        value={formatNum(meta?.analysed)}
                        sub={meta?.failed ? `${meta.failed} failed` : 'All statements processed'}
                    />
                    <StatTile icon="pi-wallet" label="Avg Monthly Balance" value={formatINR(obligation['Average Monthly Balance'])} sub="Across the statement period" />
                    <StatTile icon="pi-arrow-up" label="Avg Monthly Revenue" value={formatINR(obligation['Average Monthly Revenue'])} sub="Total inflow / months" />
                    <StatTile icon="pi-credit-card" label="Existing EMI" value={formatINR(obligation.EMI)} sub="Detected obligations" />
                </div>

                {/* Expand toggle — the full breakdown is opt-in. */}
                {analysis && (
                    <div className="flex justify-content-center mt-3">
                        <Button
                            label={expanded ? 'Hide detailed analysis' : 'View detailed analysis'}
                            icon={expanded ? 'pi pi-chevron-up' : 'pi pi-chevron-down'}
                            iconPos="right"
                            text
                            onClick={() => setExpanded((v) => !v)}
                            aria-expanded={expanded}
                            className="w-full md:w-auto"
                        />
                    </div>
                )}
            </Card>

            {!analysis ? (
                <Card>
                    <div className="flex flex-column align-items-center py-5">
                        <i className="pi pi-hourglass pi-3x text-400 mb-3" aria-hidden="true" />
                        <h3 className="text-lg font-semibold text-900 m-0 mb-2">Detailed analysis not stored</h3>
                        <p className="text-600 text-sm m-0 text-center" style={{ maxWidth: '30rem' }}>
                            This report was generated before detailed storage was enabled. Download the
                            Excel workbook for the full breakdown, or run the analysis again.
                        </p>
                    </div>
                </Card>
            ) : expanded ? (
                <Card>
                    <TabView renderActiveOnly scrollable>
                        {/* ------------------------------------------ OVERVIEW */}
                        <TabPanel header="Overview" leftIcon="pi pi-chart-bar mr-2">
                            <Panel header="Statement Period & Balances" className="mb-3">
                                <div className="grid">
                                    <InfoItem label="Period" value={months.length ? `${months[0]} to ${months[months.length - 1]}` : '—'} />
                                    <InfoItem label="Months Covered" value={formatNum(months.length)} />
                                    <InfoItem label="Opening Balance" value={totals.opening} money />
                                    <InfoItem label="Closing Balance" value={totals.closing} money />
                                </div>
                            </Panel>

                            <Panel header="Cash Flow Summary" className="mb-3">
                                <div className="grid">
                                    <InfoItem label="Total Inflow" value={totals.credit} money />
                                    <InfoItem label="Total Outflow" value={totals.debit} money />
                                    <InfoItem label="Net Cash Flow" value={totals.net} money />
                                    <InfoItem label="Avg Monthly Surplus" value={obligation['Average Monthly Surplus']} money />
                                </div>
                            </Panel>

                            {mwdBalance.length > 0 && (
                                <Panel header="Balance Trend" className="mb-3">
                                    <Chart type="line" data={balanceChart} options={chartOptions()} style={{ height: '320px' }} />
                                </Panel>
                            )}

                            {(mwdInflow.length > 0 || mwdOutflow.length > 0) && (
                                <Panel header="Inflow vs Outflow by Month" className="mb-3">
                                    <Chart type="bar" data={flowChart} options={chartOptions()} style={{ height: '320px' }} />
                                </Panel>
                            )}

                            {eodChart && (
                                <Panel header="Daily End-of-Day Balance" className="mb-3">
                                    <Chart type="line" data={eodChart} options={chartOptions()} style={{ height: '300px' }} />
                                    <p className="text-500 text-xs mt-2 mb-0">
                                        Sampled from {formatNum(Object.keys(eod).length)} daily balances for readability.
                                    </p>
                                </Panel>
                            )}

                            {Array.isArray(analysis[K.scorecard]) && analysis[K.scorecard].length > 0 && (
                                <Panel header="Scorecard" className="mb-3" toggleable>
                                    <AutoTable rows={analysis[K.scorecard]} frozenKey="Item" />
                                </Panel>
                            )}

                            {Array.isArray(analysis[K.scoring]) && analysis[K.scoring].length > 0 && (
                                <Panel header="Scoring Details" className="mb-3" toggleable collapsed>
                                    <AutoTable rows={analysis[K.scoring]} frozenKey="Description" />
                                </Panel>
                            )}
                        </TabPanel>

                        {/* -------------------------------------- TRANSACTIONS */}
                        <TabPanel header="Transactions" leftIcon="pi pi-list mr-2">
                            <Panel header="All Transactions" className="mb-3">
                                <AutoTable
                                    rows={analysis[K.txns] || []}
                                    frozenKey="Date"
                                    moneyKeys={['Amount', 'Balance']}
                                    emptyText="No transactions found"
                                    note={truncated[K.txns]
                                        ? `Showing the first ${formatNum(truncated[K.txns].kept)} of ${formatNum(truncated[K.txns].total)} transactions — download the Excel for the complete list.`
                                        : null}
                                />
                            </Panel>
                            {Array.isArray(analysis[K.topCredit]) && analysis[K.topCredit].length > 0 && (
                                <Panel header="Top 10 Credits" className="mb-3" toggleable>
                                    <AutoTable rows={analysis[K.topCredit]} frozenKey="Date" moneyKeys={['Amount', 'Balance']} />
                                </Panel>
                            )}
                            {Array.isArray(analysis[K.topDebit]) && analysis[K.topDebit].length > 0 && (
                                <Panel header="Top 10 Debits" className="mb-3" toggleable>
                                    <AutoTable rows={analysis[K.topDebit]} frozenKey="Date" moneyKeys={['Amount', 'Balance']} />
                                </Panel>
                            )}
                        </TabPanel>

                        {/* ------------------------------------------- MONTHLY */}
                        <TabPanel header="Monthly" leftIcon="pi pi-calendar mr-2">
                            {mwdBalance.length > 0 && (
                                <Panel header="Balance by Month" className="mb-3">
                                    <AutoTable
                                        rows={mwdBalance}
                                        frozenKey="Month"
                                        moneyKeys={['Monthly Opening Balance', 'Balance as on 7th', 'Balance as on 14th', 'Balance as on 21st', 'Balance as on 28th', 'Monthly Closing Balance', 'Average Bank Balance', 'Max Balance', 'Min Balance']}
                                    />
                                </Panel>
                            )}
                            {mwdInflow.length > 0 && (
                                <Panel header="Inflow by Month" className="mb-3" toggleable>
                                    <AutoTable rows={mwdInflow} frozenKey="Month" moneyKeys={['Total Credit']} />
                                </Panel>
                            )}
                            {mwdOutflow.length > 0 && (
                                <Panel header="Outflow by Month" className="mb-3" toggleable>
                                    <AutoTable rows={mwdOutflow} frozenKey="Month" moneyKeys={['Total Debits']} />
                                </Panel>
                            )}
                            {mwdBounce.length > 0 && (
                                <Panel header="Bounces by Month" className="mb-3" toggleable collapsed>
                                    <AutoTable rows={mwdBounce} frozenKey="Month" />
                                </Panel>
                            )}
                        </TabPanel>

                        {/* -------------------------------------- OBLIGATIONS */}
                        <TabPanel header="Obligations" leftIcon="pi pi-credit-card mr-2">
                            <Panel header="Income / Obligation Summary" className="mb-3">
                                <div className="grid">
                                    <InfoItem label="Loan Outstanding" value={obligation.Loan} money />
                                    <InfoItem label="Avg Monthly Revenue" value={obligation['Average Monthly Revenue']} money />
                                    <InfoItem label="Avg Monthly Surplus" value={obligation['Average Monthly Surplus']} money />
                                    <InfoItem label="Existing EMI" value={obligation.EMI} money />
                                </div>
                            </Panel>
                            {Array.isArray(analysis[K.emi]) && analysis[K.emi].length > 0 && (
                                <Panel header="EMI Details" className="mb-3">
                                    <AutoTable rows={analysis[K.emi]} />
                                </Panel>
                            )}
                            <Panel header="Recurring Debits" className="mb-3" toggleable>
                                <AutoTable
                                    rows={analysis[K.recurringDr] || []}
                                    frozenKey="Date"
                                    moneyKeys={['Amount', 'Balance']}
                                    emptyText="No recurring debits"
                                    note={truncated[K.recurringDr]
                                        ? `Showing ${formatNum(truncated[K.recurringDr].kept)} of ${formatNum(truncated[K.recurringDr].total)}.`
                                        : null}
                                />
                            </Panel>
                            <Panel header="Recurring Credits" className="mb-3" toggleable collapsed>
                                <AutoTable
                                    rows={analysis[K.recurringCr] || []}
                                    frozenKey="Date"
                                    moneyKeys={['Amount', 'Balance']}
                                    emptyText="No recurring credits"
                                    note={truncated[K.recurringCr]
                                        ? `Showing ${formatNum(truncated[K.recurringCr].kept)} of ${formatNum(truncated[K.recurringCr].total)}.`
                                        : null}
                                />
                            </Panel>
                        </TabPanel>

                        {/* ------------------------------------ FRAUD / RISK */}
                        <TabPanel header="Risk & Fraud" leftIcon="pi pi-shield mr-2">
                            <div className="grid mb-3">
                                <StatTile
                                    icon="pi-shield"
                                    label="Fraud Triggers"
                                    value={formatNum((analysis[K.fraudDetails] || []).length)}
                                    sub="Flagged transactions"
                                    severity={(analysis[K.fraudDetails] || []).length > 0 ? 'danger' : 'success'}
                                />
                                <StatTile
                                    icon="pi-undo"
                                    label="Returned Transactions"
                                    value={formatNum((analysis[K.returns] || []).length)}
                                    sub="Bounced / reversed"
                                    severity={(analysis[K.returns] || []).length > 0 ? 'danger' : 'success'}
                                />
                                <StatTile
                                    icon="pi-exclamation-triangle"
                                    label="Cheque Bounces"
                                    value={formatNum(totals.bounces)}
                                    sub="Inward + outward"
                                    severity={totals.bounces > 0 ? 'danger' : 'success'}
                                />
                                <StatTile
                                    icon="pi-verified"
                                    label="Authenticity Checks"
                                    value={formatNum((analysis[K.authenticity] || []).length)}
                                    sub="Document integrity"
                                />
                            </div>

                            {Array.isArray(analysis[K.fraudChecks]) && analysis[K.fraudChecks].length > 0 && (
                                <Panel header="Fraud Check Results" className="mb-3">
                                    <AutoTable rows={analysis[K.fraudChecks]} emptyText="No fraud checks reported" />
                                </Panel>
                            )}
                            {Array.isArray(analysis[K.fraudDetails]) && analysis[K.fraudDetails].length > 0 && (
                                <Panel header="Trigger Details" className="mb-3" toggleable>
                                    <AutoTable
                                        rows={analysis[K.fraudDetails]}
                                        frozenKey="FCU Trigger"
                                        moneyKeys={['Amount', 'Balance']}
                                        note={truncated[K.fraudDetails]
                                            ? `Showing ${formatNum(truncated[K.fraudDetails].kept)} of ${formatNum(truncated[K.fraudDetails].total)}.`
                                            : null}
                                    />
                                </Panel>
                            )}
                            {Array.isArray(analysis[K.authenticity]) && analysis[K.authenticity].length > 0 && (
                                <Panel header="Document Authenticity" className="mb-3" toggleable>
                                    <AutoTable rows={analysis[K.authenticity]} frozenKey="Item" />
                                </Panel>
                            )}
                            <Panel header="Return Transactions" className="mb-3" toggleable collapsed>
                                <AutoTable
                                    rows={analysis[K.returns] || []}
                                    frozenKey="Date"
                                    moneyKeys={['Amount', 'Balance']}
                                    emptyText="No returned transactions — good sign"
                                />
                            </Panel>
                        </TabPanel>
                    </TabView>

                    {meta?.workOrderId && (
                        <p className="text-500 text-xs mt-3 mb-0">
                            Provider reference: <span className="font-mono">{meta.workOrderId}</span>
                        </p>
                    )}
                </Card>
            ) : null}

            {/* Excel download — last thing on the page, after the analysis. */}
            {meta?.excelUrl && (
                <Card className="mt-3">
                    <div className="flex flex-column md:flex-row md:align-items-center gap-3">
                        <div className="flex-1 min-w-0">
                            <div className="text-900 text-sm font-semibold mb-1">
                                <i className="pi pi-file-excel mr-2 text-green-600" aria-hidden="true" />
                                Download the full report
                            </div>
                            <div className="text-600 text-xs">
                                Complete transaction-level workbook, including any rows trimmed from the tables above.
                            </div>
                        </div>
                        <Button
                            label="Download Excel"
                            icon="pi pi-download"
                            severity="success"
                            loading={downloading}
                            onClick={downloadExcel}
                            className="w-full md:w-auto"
                        />
                    </div>
                </Card>
            )}
        </>
    );
};

export default GlibBankStatementOutputComponent;
